/**
 * useEntitlementRefreshOnResume - Trade Loop v2.0 (Card 18, task A)
 *
 * A native user who subscribes does it in a browser, outside the app. When they come back, the
 * app is still holding the profile it loaded before they left — so it still believes they are on
 * Starter, and the features they just paid for stay locked until something refetches.
 *
 * This is that refetch, on Capacitor's `resume`.
 *
 * ⚠️ IT IS A CONVENIENCE, NEVER THE MECHANISM. Stripe webhooks are the source of truth for
 * entitlement, and they apply the subscription server-side whether or not the app is ever opened
 * again. Google warns plainly that users may not return after a link-out, and the plan has to be
 * correct in that case too. Nothing here grants anything; it only asks the server what it
 * already decided.
 *
 * Web is excluded: browsers have no resume event worth hooking, and the checkout redirect brings
 * the user back through /payment-success, which already refreshes.
 */

import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { useAuth } from '@/contexts/AuthContext';
import { isNative } from '@/utils/platform';

export function useEntitlementRefreshOnResume(): void {
  const { isAuthenticated, refreshUser } = useAuth();

  useEffect(() => {
    if (!isNative || !isAuthenticated) return;

    // `addListener` resolves asynchronously, so the handle has to be captured for cleanup rather
    // than removed directly — otherwise a fast unmount leaves the listener attached.
    const handlePromise = App.addListener('resume', () => {
      void refreshUser();
    });

    return () => {
      void handlePromise.then((handle) => handle.remove());
    };
    // `refreshUser` is deliberately not a dependency: AuthProvider redefines it on every render,
    // so depending on it would tear down and re-register the listener constantly. Re-registering
    // only when auth state flips is what is actually wanted here.
  }, [isAuthenticated]);
}
